import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CustomerHeader } from "./CustomerDashboard";
import { StatusBadge, PaymentBadge, CampaignProgress, WhatsAppButton, Loading, EmptyState, Money } from "@/components/common";
import { formatDate, formatDateTime, pendingAmount } from "@/lib/format";
import { ArrowLeft, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function CustomerOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [files, setFiles] = useState([]);
  const [payments, setPayments] = useState([]);
  const [supportWa, setSupportWa] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setSupportWa(r.data.support_whatsapp || "")).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const [o, u, f, p] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get(`/orders/${orderId}/updates`),
        api.get(`/orders/${orderId}/files`),
        api.get(`/orders/${orderId}/payments`),
      ]);
      setOrder(o.data); setUpdates(u.data); setFiles(f.data); setPayments(p.data);
    } catch { setOrder(false); }
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const download = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = file.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (order === null) return <div className="min-h-screen bg-slate-50"><CustomerHeader /><Loading /></div>;
  if (!order) return <div className="min-h-screen bg-slate-50"><CustomerHeader /><EmptyState title="Order not found" /></div>;

  const flow = ["new", "confirmed", "in_progress", "running", "completed"];

  return (
    <div className="min-h-screen bg-slate-50">
      <CustomerHeader />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <button onClick={() => navigate("/portal/orders")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> My Orders
        </button>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-bold font-display text-lg text-slate-900">{order.plan_name || order.business_name}</div>
              <div className="text-xs text-slate-500 font-mono mt-0.5">{order.order_id}</div>
            </div>
            <StatusBadge status={order.status} />
          </div>
          <div className="mt-3 text-sm text-slate-500">{formatDate(order.start_date)} → {formatDate(order.end_date)}</div>
        </div>

        <Card title="Campaign Progress"><CampaignProgress order={order} /></Card>

        <Card title="Order Status">
          <ol className="space-y-3">
            {flow.map((s, i) => {
              const done = flow.indexOf(order.status) >= i;
              const current = order.status === s;
              return (
                <li key={s} className="flex items-center gap-3">
                  <span className={`h-2.5 w-2.5 rounded-full ${current ? "bg-blue-600 ring-4 ring-blue-100" : done ? "bg-emerald-500" : "bg-slate-200"}`} />
                  <span className={`text-sm capitalize ${current ? "font-semibold text-slate-900" : done ? "text-slate-700" : "text-slate-400"}`}>{s.replace("_", " ")}</span>
                </li>
              );
            })}
          </ol>
        </Card>

        <Card title="Payment Summary">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-xs text-slate-400 uppercase">Total</div><div className="font-bold font-display text-slate-900"><Money value={order.total_amount} /></div></div>
            <div><div className="text-xs text-slate-400 uppercase">Paid</div><div className="font-bold font-display text-emerald-700"><Money value={order.paid_amount} /></div></div>
            <div><div className="text-xs text-slate-400 uppercase">Pending</div><div className="font-bold font-display text-rose-700"><Money value={pendingAmount(order)} /></div></div>
          </div>
          <div className="mt-3 flex justify-center"><PaymentBadge status={order.payment_status} /></div>
        </Card>

        <Card title="Updates">
          {updates.length === 0 ? <p className="text-sm text-slate-500">No updates yet.</p> : (
            <ol className="space-y-3">
              {updates.map((u) => (
                <li key={u.id} className="border-l-2 border-blue-500 pl-3">
                  <div className="text-sm font-medium text-slate-900">{u.title}</div>
                  {u.message && <div className="text-sm text-slate-600">{u.message}</div>}
                  <div className="text-xs text-slate-400 mt-0.5">{formatDateTime(u.created_at)}</div>
                </li>
              ))}
            </ol>
          )}
        </Card>

        {files.length > 0 && (
          <Card title="Files">
            <div className="space-y-2">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                  <span className="h-9 w-9 rounded-lg bg-slate-100 grid place-items-center text-slate-600"><FileText className="h-4 w-4" /></span>
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-900 truncate">{f.original_filename}</div><div className="text-xs text-slate-500 capitalize">{f.category}</div></div>
                  <button onClick={() => download(f)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`cust-download-${f.id}`}><Download className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {supportWa && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col items-center text-center">
            <p className="text-sm text-slate-600 mb-3">Questions about your campaign? Reach your campaign manager.</p>
            <WhatsAppButton number={supportWa} label="Chat with Campaign Manager" message={`Hi, I have a question about my order ${order.order_id}`} />
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <h3 className="font-semibold font-display text-slate-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}
